import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-polyglot';
import List from '../List';
import Dropdown from '../Dropdown';
import Link from '../Link';
import SvgIcon from '../SvgIcon';
import {
    xShareUrl,
    facebookShareUrl,
    emailShareUrl,
    whatsappWebUrl,
    whatsappMobileUrl,
    linkedInShareUrl,
} from '../../lib/social';
import useDeviceType from '../../hooks/useDeviceType';

const ShareMenu = ({
    t,
    title,
    thumbUrl,
    contentUrl,
    href,
    align,
    onClick,
    videoShare,
    isIllustration,
    readingList,
    landingPageBook,
}) => {
    // const { isUserAgentMobile: isMobile } = useDeviceType();
    const [isMobile, setIsMobile] = useState(false);
    const [isMobileScreen, setIsMobileScreen] = useState(
        window.innerWidth <= 768
    );
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const checkIsMobileDevice = () => {
            const isMobileDevice =
                /Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(
                    navigator.userAgent
                );
            setIsMobile(isMobileDevice);
        };
        checkIsMobileDevice();

        const handleResize = () => {
            setIsMobileScreen(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleNativeShare = (title, url) => {
        if (navigator.share) {
            navigator
                .share({
                    title,
                    //   text: summary,
                    url,
                })
                .catch(() => {});
        } else {
            console.log('Native sharing is not supported on this device.');
        }
    };

    const copyLinkToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(href);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
        } catch (err) {
            alert('Failed to copy link. Please copy manually.');
        }
    };

    const encodedUrl = encodeURIComponent(href);

    return (
        <Dropdown
            align={align}
            toggleEl={
                !isMobile ? (
                    <Link
                        favouriteShare={true}
                        normal
                        dataGTM={
                            isIllustration ? 'illustration-share' : 'book-share'
                        }
                    >
                        {!videoShare && !landingPageBook && (
                            <SvgIcon name={'Share'} size="reader-view-share" />
                        )}
                        {landingPageBook && (
                            <div>
                                <SvgIcon name="Share" size="mi" shareIcon />
                            </div>
                        )}
                        {t('global.share', 1)}
                    </Link>
                ) : (
                    <Link
                        favouriteShare={true}
                        normal
                        dataGTM={
                            isIllustration ? 'illustration-share' : 'book-share'
                        }
                    >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <SvgIcon name={'Share'} size="reader-view-share" />
                            <span>{t('global.share', 1)}</span>
                        </div>
                    </Link>
                )
            }
        >
            <List nowrap socialShareIcons>
                <Link
                    fullWidthShare
                    href={xShareUrl(href)}
                    shouldOpenNewPage={true}
                    onClick={onClick}
                    dataGTM={
                        isIllustration
                            ? 'illustration-twitter-share'
                            : 'book-twitter-share'
                    }
                >
                    <SvgIcon
                        name="X"
                        size="m"
                        pushRight
                        dataGTM={
                            isIllustration
                                ? 'illustration-twitter-share'
                                : 'book-twitter-share'
                        }
                    />{' '}
                    {t('global.twitter')}
                </Link>
                <Link
                    fullWidthShare
                    href={facebookShareUrl(href)}
                    shouldOpenNewPage={true}
                    onClick={onClick}
                    dataGTM={
                        isIllustration
                            ? 'illustration-facebook-share'
                            : 'book-facebook-share'
                    }
                >
                    <SvgIcon
                        name="facebook"
                        size="m"
                        pushRight
                        dataGTM={
                            isIllustration
                                ? 'illustration-facebook-share'
                                : 'book-facebook-share'
                        }
                    />{' '}
                    {t('global.facebook')}
                </Link>
                <Link
                    fullWidthShare
                    href={emailShareUrl(href, title)}
                    shouldOpenNewPage={true}
                    onClick={onClick}
                    dataGTM={
                        isIllustration
                            ? 'illustration-email-share'
                            : 'book-email-share'
                    }
                >
                    <SvgIcon
                        name="mail"
                        size="m"
                        pushRight
                        dataGTM={
                            isIllustration
                                ? 'illustration-email-share'
                                : 'book-email-share'
                        }
                    />{' '}
                    {t('global.mailTo')}
                </Link>
                <Link
                    fullWidthShare
                    href={
                        isMobile && isMobileScreen
                            ? whatsappMobileUrl(encodedUrl)
                            : whatsappWebUrl(encodedUrl)
                    }
                    shouldOpenNewPage={true}
                    onClick={onClick}
                    dataGTM={
                        isIllustration
                            ? 'illustration-whats-app-share'
                            : 'book-whats-app-share'
                    }
                >
                    <SvgIcon
                        name="whatsapp"
                        size="m"
                        pushCenter
                        dataGTM={
                            isIllustration
                                ? 'illustration-whats-app-share'
                                : 'book-whats-app-share'
                        }
                    />{' '}
                    {isMobile && isMobileScreen
                        ? t('share.whatsapp')
                        : t('share.whatsappWeb')}
                </Link>
                <Link
                    fullWidthShare
                    href={linkedInShareUrl(href)}
                    shouldOpenNewPage={true}
                    onClick={onClick}
                >
                    <SvgIcon
                        name="mail"
                        size="m"
                        pushCenter
                        dataGTM={
                            isIllustration
                                ? 'illustration-email-share'
                                : 'book-email-share'
                        }
                    />{' '}
                    {'Linkedin'}
                </Link>
                {isMobile && isMobileScreen && navigator.share && (
                    <Link
                        fullWidthShare
                        shouldOpenNewPage={false}
                        onClick={() => handleNativeShare(title, href)}
                    >
                        <SvgIcon
                            name="chain"
                            size="m"
                            pushCenter
                            dataGTM={
                                isIllustration
                                    ? 'illustration-email-share'
                                    : 'book-email-share'
                            }
                        />{' '}
                        {t('share.copyLink')}
                    </Link>
                )}
                {window.matchMedia('(min-width: 1024px)').matches && (
                    <Link
                        fullWidthShare
                        shouldOpenNewPage={false}
                        onClick={copyLinkToClipboard}
                    >
                        <SvgIcon
                            name="chain"
                            size="m"
                            pushCenter
                            dataGTM={
                                isIllustration
                                    ? 'illustration-email-share'
                                    : 'book-email-share'
                            }
                        />{' '}
                        {copied ? (
                            <span style={{ color: 'green' }}>
                                {t('share.copied')}
                            </span>
                        ) : (
                            t('share.copyLink')
                        )}
                    </Link>
                )}
            </List>
        </Dropdown>
    );
};

ShareMenu.propTypes = {
    t: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    thumbUrl: PropTypes.string,
    contentUrl: PropTypes.string,
    href: PropTypes.string.isRequired,
    align: PropTypes.oneOf(['left', 'right', 'center']),
    onClick: PropTypes.func,
    videoShare: PropTypes.bool,
    isIllustration: PropTypes.bool,
    readingList: PropTypes.bool,
    landingPageBook: PropTypes.bool,
};

export default translate()(ShareMenu);
